import merge from 'lodash-es/merge'

import { ShoppingCartItem } from '@rewardops/api/src/router/cart'
import { getItemPricing } from '@rewardops/utils'

import { getAttr } from './base-utils'

const supplierItemsPath = ['supplier_items', 0]

export interface ItemType {
  id: string
  total: string
  quantity: string
  brand: string
  name: string
  imageUrl: string
  itemTotal: number
  regularPrice: number
  salePrice: number
  orderToken: string
  sivId: string
}

export const itemTargetAttrs = [
  {
    key: 'id',
    path: 'id',
  },
  {
    key: 'quantity',
    path: 'quantity',
    defaultValue: 1,
    transform: (value: any) => parseInt(value, 10) || 1,
  },
  {
    key: 'name',
    path: 'name',
  },
  {
    key: 'imageUrl',
    path: ['images', 0, 'url'],
  },
  {
    key: 'regularPrice',
    path: (programCurrency: string) => [
      ...supplierItemsPath,
      'prices',
      'regular_price',
      programCurrency,
    ],
    defaultValue: 0,
    transform: (value: any) => parseFloat(value) || 0,
  },
  {
    key: 'salePrice',
    path: (programCurrency: string) => [
      ...supplierItemsPath,
      'prices',
      'sale_price',
      programCurrency,
    ],
    defaultValue: 0,
    transform: (value: any) => parseFloat(value) || 0,
  },
  {
    key: 'orderToken',
    path: [...supplierItemsPath, 'order_token'],
    defaultValue: '',
  },
  {
    key: 'sivId',
    path: [...supplierItemsPath, 'id'],
    defaultValue: '',
  },
  {
    key: 'supplier_items',
    path: ['supplier_items'],
    defaultValue: [],
  },
]

export const parseCart = (cart: any, programCurrency: string) => {
  const cartItems = cart?.cartItems || []

  const items = extractCartItems(cartItems, programCurrency)

  return {
    id: cart?.id,
    items,
    hasCash: false,
    cashTotal: 0,
  }
}

export const extractCartItems = (
  items: object[],
  programCurrency: string,
): ItemType[] => {
  return items.map((item) => {
    const getAttrFromObj = getAttr(item, programCurrency)
    const attrs = (itemTargetAttrs as any).map(getAttrFromObj)
    return merge({}, ...attrs) as ItemType
  })
}

export const calculateCartPointsCost = (
  items: object[],
  programCurrency: string,
): number => {
  return (
    items?.reduce((acc: number, item: any) => {
      const pricing = getItemPricing(item, programCurrency)
      const itemPrice = pricing.price
      return acc + itemPrice * item.quantity
    }, 0) ?? 0
  )
}

export const hasCartLimitExceeded = (
  items: ShoppingCartItem[],
  itemBeingUpdated: { sivId: number; quantity: number },
  limit: number = 50,
  directCalculation: boolean = false,
): boolean => {
  // Calculate the total quantity currently in the cart
  const totalQuantity = items?.reduce((total, item) => total + item.quantity, 0)

  if (directCalculation)
    return totalQuantity + itemBeingUpdated?.quantity > limit

  // Find the existing item in the cart that matches the sivId of the itemBeingUpdated
  const existingItem = items?.find(
    (item) => item.siv_id === itemBeingUpdated.sivId,
  )

  // Calculate the new total quantity after adding the itemBeingUpdated
  const newTotalQuantity =
    totalQuantity - (existingItem?.quantity || 0) + itemBeingUpdated.quantity
  return newTotalQuantity > limit
}

export const hasItemLimitExceeded = (
  items: ShoppingCartItem[],
  itemBeingUpdated: { sivId: number; quantity: number },
  itemLimit: number = 10,
): boolean => {
  const isValidSelection = items?.some(
    (item) =>
      item.siv_id === itemBeingUpdated.sivId &&
      itemBeingUpdated?.quantity + item?.quantity > itemLimit,
  )

  return isValidSelection
}
